import { Button, Combobox, Textarea } from '@ibsys/kit';
import { FC, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartRuleColumn } from '@/entities/dashboards';
import { chartTemplatePlaces, withInsertedPlace, withPlaceValue, type ChartTemplatePlace } from './chartTemplatePlaces';

type Props = {
  /** Текст результата вместе с местами подстановки значений выборки */
  value: string;
  onChange: (template: string) => void;
  /** Значения выборки, доступные тексту результата */
  columns: ChartRuleColumn[];
};

/**
 * Текст результата показателя и сводки: сам текст и места подстановки значений выборки.
 *
 * Место подстановки задаётся выбором значения из перечня, а не набором его имени: перечень
 * приходит вместе с правилом и открыт всякому, кто правит дашборд, тогда как сам запрос закрыт
 * разрешением. По каждому месту видно подставленное в него значение, и оно меняется тут же —
 * не открывая запрос и не переписывая текст руками.
 *
 * Место, значения которого в выборке нет, из перечня не пропадает: текст мог быть набран до
 * правки запроса, и пользователю нужно видеть, что подстановка осталась ни на что не указывающей.
 */
export const ChartTemplateField: FC<Props> = ({ value, onChange, columns }) => {
  const { t } = useTranslation('qualityDashboard');
  // Место, куда встаёт выбранное значение: положение курсора запоминается, пока поле в фокусе.
  const caretRef = useRef<number | null>(null);

  const places = useMemo(() => chartTemplatePlaces(value), [value]);

  const columnOptions = useMemo(
    () => columns.map((column) => ({ value: column.name, label: column.name, description: column.type })),
    [columns],
  );

  const placeOptions = (place: ChartTemplatePlace) =>
    columns.some((column) => column.name === place.name)
      ? columnOptions
      : [
          ...columnOptions,
          { value: place.name, label: place.name, description: t('presets.editor.templateUnknownValue') },
        ];

  const insertPlace = (name: string) => {
    const at = caretRef.current ?? value.length;
    caretRef.current = at + name.length + 2;
    onChange(withInsertedPlace(value, at, name));
  };

  const changePlace = (place: ChartTemplatePlace, name: unknown) => {
    if (typeof name !== 'string' || name === place.name) return;
    onChange(withPlaceValue(value, place, name));
  };

  return (
    <div className="flex flex-col gap-3">
      <Textarea
        label={t('presets.editor.chartTemplate')}
        description={t('presets.editor.chartTemplateHint')}
        value={value}
        onChange={(event) => {
          caretRef.current = event.target.selectionStart;
          onChange(event.target.value);
        }}
        onSelect={(event) => {
          caretRef.current = event.currentTarget.selectionStart;
        }}
        rows={2}
      />

      {columns.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('presets.editor.templateValues')}</span>
          {columns.map((column) => (
            <Button key={column.name} variant="secondary" size="sm" onClick={() => insertPlace(column.name)}>
              {column.name}
            </Button>
          ))}
        </div>
      )}

      {places.length > 0 && (
        <div className="grid grid-cols-1 gap-2 @md/chart-properties:grid-cols-2">
          {places.map((place, index) => (
            <Combobox
              key={`${place.start}-${place.name}`}
              label={t('presets.editor.templatePlace', { number: index + 1 })}
              value={place.name}
              options={placeOptions(place)}
              onChange={(name) => changePlace(place, name)}
              isSearchable={false}
              className="w-full"
            />
          ))}
        </div>
      )}
    </div>
  );
};
